/**
 * useHypeStatScraper - Hook para integração HypeStat
 *
 * Gerencia browser, scraping e polling de status do HypeStat.
 * HypeStat agrega dados de múltiplas fontes: HypeStat, SEMrush e SimilarWeb.
 * Seleciona automaticamente o MAIOR valor de audiência.
 *
 * Vantagens:
 * - Sem necessidade de login (público)
 * - Múltiplas fontes de dados
 * - Scraping mais simples e confiável
 */
import { useState, useEffect, useCallback } from 'react';
import {
  hypeStatOpenBrowser,
  hypeStatCloseBrowser,
  hypeStatCheckStatus,
  hypeStatScrape,
  hypeStatGetProgress,
  hypeStatStop
} from '../../services/api';

export const useHypeStatScraper = (onStatsUpdate) => {
  const [browserOpen, setBrowserOpen] = useState(false);
  const [status, setStatus] = useState({
    isRunning: false,
    progress: { current: 0, total: 0, domain: '', status: 'idle' }
  });
  const [loading, setLoading] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [scrapingResults, setScrapingResults] = useState(null);
  const [initialized, setInitialized] = useState(false);

  /**
   * Verificar status do browser e scraping
   */
  const checkStatus = useCallback(async () => {
    try {
      const response = await hypeStatCheckStatus();
      setBrowserOpen(response.data.browserOpen);
      setStatus({
        isRunning: response.data.isRunning,
        progress: response.data.progress || { current: 0, total: 0, domain: '', status: 'idle' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      setBrowserOpen(false);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Abrir browser para scraping
   */
  const openBrowser = async () => {
    try {
      setLoading(true);
      const response = await hypeStatOpenBrowser();

      if (response.data.success) {
        setBrowserOpen(true);
        return { success: true, message: 'Browser aberto e pronto para scraping' };
      }

      return { success: false, error: response.data.message };
    } catch (error) {
      return { success: false, error: 'Erro ao abrir browser: ' + error.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fechar browser
   */
  const closeBrowser = async () => {
    try {
      setLoading(true);
      const response = await hypeStatCloseBrowser();

      if (response.data.success) {
        setBrowserOpen(false);
        return { success: true, message: 'Browser fechado' };
      }

      return { success: false, error: response.data.message };
    } catch (error) {
      return { success: false, error: 'Erro ao fechar browser: ' + error.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Iniciar scraping de domínios
   */
  const startScraping = async (domainsToScrape, allDomains = []) => {
    // Usa selecionados ou todos
    const domains = domainsToScrape.length > 0
      ? domainsToScrape
      : allDomains.map(d => d.registrableDomain || d.domain);

    if (domains.length === 0) {
      return { success: false, error: 'Nenhum domínio para processar' };
    }

    try {
      setLoading(true);
      const response = await hypeStatScrape(domains);

      if (response.data.success) {
        setSelectedDomains([]); // Limpa seleção

        // Inicia polling do status
        let lastStatsUpdate = 0;
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await hypeStatGetProgress();
            setStatus(statusRes.data);

            const currentProgress = statusRes.data?.progress?.current || 0;

            // Atualiza stats a cada 3 domínios
            if (currentProgress > 0 && currentProgress - lastStatsUpdate >= 3) {
              if (onStatsUpdate) onStatsUpdate();
              lastStatsUpdate = currentProgress;
            }

            // Para polling quando scraping terminar
            if (!statusRes.data.isRunning) {
              clearInterval(pollInterval);
              
              // ✅ Capturar resultados quando scraping terminar
              if (statusRes.data.results) {
                setScrapingResults(statusRes.data.results);
              }
              
              if (onStatsUpdate) onStatsUpdate(); // Update final
            }
          } catch {
            clearInterval(pollInterval);
          }
        }, 2000);

        return {
          success: true,
          message: `Scraping iniciado para ${response.data.totalDomains} domínios`
        };
      }

      return { success: false, error: 'Falha ao iniciar scraping' };
    } catch (error) {
      return { success: false, error: 'Erro ao iniciar scraping: ' + error.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Parar scraping
   */
  const stopScraping = async () => {
    try {
      await hypeStatStop();
      setStatus({ isRunning: false, progress: { ...status.progress, status: 'stopped' } });
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Erro ao parar scraping: ' + error.message };
    }
  };

  /**
   * Limpar resultados do scraping
   */
  const clearResults = useCallback(() => {
    setScrapingResults(null);
  }, []);

  /**
   * Adicionar/remover domínio da seleção
   */
  const toggleDomain = useCallback((domain) => {
    const domainKey = (domain.registrableDomain || domain.domain).toLowerCase().trim();
    setSelectedDomains(prev =>
      prev.includes(domainKey)
        ? prev.filter(d => d !== domainKey)
        : [...prev, domainKey]
    );
  }, []);

  /**
   * Selecionar todos os domínios
   */
  const toggleSelectAll = useCallback((allDomains) => {
    setSelectedDomains(prev => {
      if (prev.length === allDomains.length) {
        return [];
      } else {
        return allDomains.map(d => (d.registrableDomain || d.domain).toLowerCase().trim());
      }
    });
  }, []);

  // Verifica status ao montar
  useEffect(() => {
    const initializeStatus = async () => {
      await checkStatus();
      setInitialized(true);
    };
    initializeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // Estados
    browserOpen,
    status,
    loading,
    selectedDomains,
    initialized,
    scrapingResults,

    // Ações
    checkStatus,
    openBrowser,
    closeBrowser,
    startScraping,
    stopScraping,
    toggleDomain,
    toggleSelectAll,
    clearResults
  };
};
